import type {
  Lead,
  LeadStatus,
  LeadUpdateInput,
} from "@chat-yai/shared/admin-types";
import type { PaginatedResponse } from "@chat-yai/shared";
import { request } from "@/api/client";

export interface LeadListQuery {
  status?: LeadStatus;
  q?: string;
  from?: string;
  to?: string;
  page?: number;
  page_size?: number;
  chatbot_id?: string;
  assignee_user_id?: string | null;
}

export const leadsApi = {
  list(query: LeadListQuery = {}) {
    return request<PaginatedResponse<Lead>>("/admin/leads", {
      query: query as Record<string, string | number | boolean | undefined | null>,
    });
  },
  update(id: string, input: LeadUpdateInput) {
    return request<{ lead: Lead }>(`/admin/leads/${id}`, {
      method: "PATCH",
      body: input,
    });
  },
  // ---- WP-D: server-side CSV export -------------------------------------
  // Returns a fully-qualified URL the browser can navigate to. The server
  // will set Content-Disposition so the browser saves a file. We add the
  // current bearer token via short-lived `?access_token=` query string —
  // the real backend re-validates it. In dev the MSW handler also accepts
  // it via the standard Authorization header, but `<a target=_blank>` on
  // some browsers strips Authorization, so the query parameter is the
  // portable fallback.
  exportCsvUrl(query: LeadListQuery & { access_token?: string }) {
    const params = new URLSearchParams();
    params.set("format", "csv");
    if (query.status) params.set("status", query.status);
    if (query.from) params.set("from", query.from);
    if (query.to) params.set("to", query.to);
    if (query.chatbot_id) params.set("chatbot_id", query.chatbot_id);
    if (query.assignee_user_id) {
      params.set("assignee_user_id", query.assignee_user_id);
    }
    if (query.q) params.set("q", query.q);
    if (query.access_token) params.set("access_token", query.access_token);
    return `/api/admin/leads/export?${params.toString()}`;
  },
};
